import React, { useState } from 'react';

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Background with gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-midnight via-deep-purple to-royal-purple">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1586023492125-27b2c045efd7?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80')] bg-cover bg-center bg-no-repeat opacity-20"></div>
        </div>

        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {/* Breadcrumb */}
            <div className="flex items-center justify-center space-x-2 mb-8">
              <span className="text-lavender/60">Home</span>
              <span className="text-lavender/40">/</span>
              <span className="text-lavender">Contact</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-serif font-bold text-white mb-8 animate-fade-in drop-shadow-2xl">
              Get in Touch
            </h1>
            
            {/* Subtitle */}
            <p className="text-xl md:text-2xl lg:text-3xl text-lavender mb-10 font-medium animate-slide-up drop-shadow-xl max-w-3xl mx-auto">
              Let's discuss your textile vision
            </p>
            
            {/* Description */}
            <p className="text-lg md:text-xl text-white/90 mb-12 max-w-4xl mx-auto leading-relaxed animate-slide-up drop-shadow-lg">
              Ready to bring your textile dreams to life? Our team of experts is here to help you find 
              the perfect fabrics, discuss custom solutions, or answer any questions about our collections. 
              Reach out and let's start creating something extraordinary together.
            </p>

            {/* Contact Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-serif font-bold text-gradient mb-2">24h</div>
                <div className="text-lavender/80">Response Time</div>
              </div>
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-serif font-bold text-gradient mb-2">100%</div>
                <div className="text-lavender/80">Satisfaction</div>
              </div>
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-serif font-bold text-gradient mb-2">Free</div>
                <div className="text-lavender/80">Consultation</div>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute bottom-8 left-8 hidden lg:block">
          <div className="w-32 h-32 border-2 border-lavender/30 rotate-45 rounded-3xl animate-float"></div>
        </div>
        <div className="absolute top-20 right-12 hidden lg:block">
          <div className="w-20 h-20 bg-gradient-to-br from-warm-gold/20 to-sunset-orange/20 rounded-full border border-warm-gold/30 animate-pulse-slow"></div>
        </div>
      </section>

      {/* Contact Information & Form */}
      <section className="py-20 bg-gradient-primary">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left Column - Contact Info */}
            <div>
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-gradient mb-8">
                Let's Connect
              </h2>
              <p className="text-lg text-sage mb-12 leading-relaxed">
                Whether you're looking for specific fabrics, need custom solutions, or just want to learn more 
                about our collections, we're here to help. Reach out through any of these channels.
              </p>

              {/* Contact Methods */}
              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="w-14 h-14 bg-gradient-to-r from-royal-purple/20 to-amethyst/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl">📍</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-serif font-semibold text-charcoal mb-2">Visit Our Studio</h3>
                    <div className="text-sage space-y-1">
                      Manhattan, NY
                    </div>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-14 h-14 bg-gradient-to-r from-warm-gold/20 to-sunset-orange/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl">📞</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-serif font-semibold text-charcoal mb-2">Call Us</h3>
                    <div className="text-sage space-y-1">
                      <p>Mon-Fri: 9AM - 6PM EST</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-14 h-14 bg-gradient-to-r from-ocean-blue/20 to-teal-wave/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl">✉️</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-serif font-semibold text-charcoal mb-2">Email Us</h3>
                    <div className="text-sage space-y-1">
                      <p>hello@vogueloom.org</p>
                      <p>We respond within 24 hours</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="mt-12">
                <h3 className="text-xl font-serif font-semibold text-charcoal mb-4">Follow Our Journey</h3>
                <div className="flex space-x-4">
                  {[
                    { name: 'Instagram', icon: '📸', color: 'from-pink-500 to-purple-500' },
                    { name: 'Pinterest', icon: '📌', color: 'from-red-500 to-pink-500' },
                    { name: 'LinkedIn', icon: '💼', color: 'from-blue-500 to-indigo-500' }
                  ].map((social) => (
                    <a
                      key={social.name}
                      href="#"
                      className={`w-12 h-12 bg-gradient-to-r ${social.color} text-white rounded-2xl flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300`}
                      aria-label={social.name}
                    >
                      <span className="text-xl">{social.icon}</span>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Contact Form */}
            <div className="glass-card p-8 border-2 border-lavender/40">
              <h3 className="text-2xl font-serif font-semibold text-charcoal mb-6">Send Us a Message</h3>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-sage mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-lavender/30 rounded-2xl bg-white/50 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-royal-purple/50 focus:border-royal-purple transition-all duration-300"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-sage mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-lavender/30 rounded-2xl bg-white/50 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-royal-purple/50 focus:border-royal-purple transition-all duration-300"
                    placeholder="Enter your email address"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-sage mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 border border-lavender/30 rounded-2xl bg-white/50 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-royal-purple/50 focus:border-royal-purple transition-all duration-300 resize-none"
                    placeholder="Tell us about your project or inquiry..."
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-royal-purple to-amethyst text-white py-4 rounded-2xl font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 border border-royal-purple/30"
                >
                  Send Message
                </button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-sage">
                  By submitting this form, you agree to our{' '}
                  <a href="#" className="text-royal-purple hover:text-deep-purple underline">
                    privacy policy
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-gradient-secondary">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-gradient-gold mb-6">
              Find Our Studio
            </h2>
            <p className="text-xl text-sage max-w-2xl mx-auto">
              Located in the heart of the design district, our studio is easily accessible and open for visits
            </p>
          </div>

          {/* Interactive Map */}
          <div className="bg-gradient-to-br from-lavender/20 to-amethyst/20 rounded-3xl p-8 border border-lavender/30">
            <div className="aspect-video bg-white rounded-2xl overflow-hidden border border-lavender/20 shadow-xl">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d193595.25436351647!2d-74.11976404999999!3d40.69766374999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c24fa5d33f083b%3A0xc80b8f06e177fe62!2sNew%20York%2C%20NY%2C%20USA!5e0!3m2!1sen!2sus!4v1703123456789!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Vogueloom Studio Location - New York, NY"
                className="w-full h-full"
              ></iframe>
            </div>
          </div>

          {/* Additional Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
            <div className="glass-card p-6 text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-warm-gold/20 to-sunset-orange/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🚗</span>
              </div>
              <h3 className="text-lg font-serif font-semibold text-charcoal mb-2">Free Parking</h3>
              <p className="text-sage text-sm">Convenient parking available for all visitors</p>
            </div>

            <div className="glass-card p-6 text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-ocean-blue/20 to-teal-wave/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">♿</span>
              </div>
              <h3 className="text-lg font-serif font-semibold text-charcoal mb-2">Accessible</h3>
              <p className="text-sage text-sm">Fully accessible studio for all visitors</p>
            </div>

            <div className="glass-card p-6 text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-royal-purple/20 to-amethyst/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">☕</span>
              </div>
              <h3 className="text-lg font-serif font-semibold text-charcoal mb-2">Refreshments</h3>
              <p className="text-sage text-sm">Complimentary coffee and tea for guests</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
